import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { OnboardingTourSlides, useOnboardingTourSlides } from "@/components/OnboardingTourSlides";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Layers, 
  Link2, 
  MessageSquare, 
  ArrowRight, 
  Check,
  Sparkles,
  Rocket,
  BookOpen,
  Globe,
  Youtube
} from "lucide-react";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { mastermindSpacesService } from "@/services/mastermindSpacesService";
import { sourcesService } from "@/services/sourcesService";
import type { MastermindSpace, Source } from "@shared/schema";

interface OnboardingWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void;
}

const steps = [
  {
    id: 1,
    title: "Create Your First Space",
    description: "Spaces organize your knowledge by topic or project",
    icon: Layers,
    color: "text-blue-500",
  },
  {
    id: 2,
    title: "Add Your First Source",
    description: "Sources are the content you want to learn from",
    icon: Link2,
    color: "text-green-500",
  },
  {
    id: 3,
    title: "Start an AI Chat",
    description: "Ask questions about your sources and get insights",
    icon: MessageSquare,
    color: "text-purple-500",
  },
];

const sourceTypes = [
  { type: "youtube", label: "YouTube Video", icon: Youtube, placeholder: "https://youtube.com/watch?v=..." },
  { type: "article", label: "Web Article", icon: Globe, placeholder: "https://example.com/article" },
  { type: "manual", label: "Manual Note", icon: BookOpen, placeholder: "Enter your own content..." },
];

export function OnboardingWizard({ open, onOpenChange, onComplete }: OnboardingWizardProps) {
  const { toast } = useToast();
  const { currentWorkspaceId } = useWorkspace();
  const { completeMilestone, hasMilestone } = useOnboarding();
  const [currentStep, setCurrentStep] = useState(1);
  // DS EDIT 12 — CMS tour phase (skippable; renders only when live slides exist)
  const [tourDone, setTourDone] = useState(false);
  const tourSlides = useOnboardingTourSlides().data ?? [];
  const [spaceName, setSpaceName] = useState("");
  const [spaceDescription, setSpaceDescription] = useState("");
  const [createdSpace, setCreatedSpace] = useState<MastermindSpace | null>(null);
  const [sourceType, setSourceType] = useState<"youtube" | "article" | "manual">("article");
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourceTitle, setSourceTitle] = useState("");
  const [sourceContent, setSourceContent] = useState("");
  const [createdSource, setCreatedSource] = useState<Source | null>(null);

  const progress = ((currentStep - 1) / steps.length) * 100;

  const createSpaceMutation = useMutation({
    mutationFn: async (data: { name: string; description: string }) => {
      return await mastermindSpacesService.createMastermindSpace(
        {
          name: data.name,
          description: data.description || null,
          icon: "Brain",
          color: "hsl(220, 70%, 50%)",
          goals: [],
          agents: [],
          linkedSourceIds: [],
          milestones: [],
        },
        currentWorkspaceId ?? undefined
      );
    },
    onSuccess: (data: MastermindSpace) => {
      setCreatedSpace(data);
      // Invalidate all spaces queries (matching MastermindSpaces.tsx pattern)
      queryClient.invalidateQueries({ queryKey: ["spaces"] }); // For AppSidebar
      queryClient.invalidateQueries({ queryKey: ["/api/spaces"] }); // For spaces page
      queryClient.invalidateQueries({ queryKey: ["/api/mastermind-spaces-personal"] }); // For personal spaces check
      
      // Trigger first_space milestone for new users (unlocks Library)
      if (!hasMilestone('first_space')) {
        completeMilestone('first_space');
      }
      
      setCurrentStep(2);
      toast({
        title: "Space Created!",
        description: `"${data.name}" is ready for your knowledge.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating space",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const createSourceMutation = useMutation({
    mutationFn: async (data: { 
      sourceType: string; 
      url?: string; 
      title?: string; 
      content?: string;
      linkedSpaceIds?: string[];
    }) => {
      const sourceData: any = {
        title: data.title || (data.url ? "New Source" : "Untitled Note"),
        content: data.content || "",
        type: data.sourceType,
        url: data.url || null,
        topics: [],
        subTopics: [],
        tags: [],
        linkedThreads: [],
        linkedSpaceIds: data.linkedSpaceIds || [],
      };
      
      return await sourcesService.createSource(sourceData, currentWorkspaceId ?? null);
    },
    onSuccess: async (data: Source) => {
      setCreatedSource(data);
      
      // If we created a space, link the source to it by updating the space's linkedSourceIds
      if (createdSpace && data.id) {
        try {
          const existingLinkedIds = createdSpace.linkedSourceIds || [];
          const updatedLinkedIds = Array.from(new Set([...existingLinkedIds, data.id]));
          
          await mastermindSpacesService.updateMastermindSpace(
            createdSpace.id,
            { linkedSourceIds: updatedLinkedIds },
            currentWorkspaceId ?? undefined
          );
          
          // Invalidate spaces queries to refresh the space data
          queryClient.invalidateQueries({ queryKey: ["spaces"] });
          queryClient.invalidateQueries({ queryKey: ["/api/spaces"] });
        } catch (error) {
          console.error("Failed to link source to space:", error);
          // Don't block the flow if this fails
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ["/api/sources"] });
      setCurrentStep(3);
      toast({
        title: "Source Added!",
        description: `"${data.title || 'New source'}" has been added.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding source",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleCreateSpace = () => {
    if (!spaceName.trim()) {
      toast({
        title: "Name required",
        description: "Please enter a name for your space.",
        variant: "destructive",
      });
      return;
    }
    createSpaceMutation.mutate({
      name: spaceName,
      description: spaceDescription || `My first knowledge space`,
    });
  };

  const handleAddSource = () => {
    if (sourceType === "manual") {
      if (!sourceTitle.trim()) {
        toast({
          title: "Title required",
          description: "Please enter a title for your note.",
          variant: "destructive",
        });
        return;
      }
      createSourceMutation.mutate({
        sourceType: "manual",
        title: sourceTitle,
        content: sourceContent,
        linkedSpaceIds: createdSpace ? [createdSpace.id] : [],
      });
    } else {
      if (!sourceUrl.trim()) {
        toast({
          title: "URL required",
          description: "Please enter a URL for your source.",
          variant: "destructive",
        });
        return;
      }
      createSourceMutation.mutate({
        sourceType,
        url: sourceUrl,
        linkedSpaceIds: createdSpace ? [createdSpace.id] : [],
      });
    }
  };

  const handleComplete = () => {
    onOpenChange(false);
    onComplete?.();
    window.location.href = `/spaces/${createdSpace?.id}`;
  };

  const handleSkipToChat = () => {
    onOpenChange(false);
    window.location.href = "/chat";
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="space-name">What are you learning about?</Label>
              <Input
                id="space-name"
                placeholder="e.g., Machine Learning Basics, Business Strategy, Language Learning"
                value={spaceName}
                onChange={(e) => setSpaceName(e.target.value)}
                data-testid="input-onboarding-space-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="space-description">Brief description (optional)</Label>
              <Textarea
                id="space-description"
                placeholder="What's your goal with this space?"
                value={spaceDescription}
                onChange={(e) => setSpaceDescription(e.target.value)}
                rows={2}
                data-testid="input-onboarding-space-description"
              />
            </div>
            <Button 
              onClick={handleCreateSpace} 
              className="w-full"
              disabled={createSpaceMutation.isPending}
              data-testid="button-onboarding-create-space"
            >
              {createSpaceMutation.isPending ? (
                "Creating..."
              ) : (
                <>
                  Create Space <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <Check className="h-4 w-4 text-green-500" />
              Space "{createdSpace?.name}" created!
            </div>

            <div className="space-y-2">
              <Label>What type of source?</Label>
              <div className="grid grid-cols-3 gap-2">
                {sourceTypes.map((st) => (
                  <Button
                    key={st.type}
                    variant={sourceType === st.type ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSourceType(st.type as typeof sourceType)}
                    className="flex flex-col h-auto py-3 gap-1"
                    data-testid={`button-source-type-${st.type}`}
                  >
                    <st.icon className="h-4 w-4" />
                    <span className="text-xs">{st.label}</span>
                  </Button>
                ))}
              </div>
            </div>

            {sourceType === "manual" ? (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="source-title">Note Title</Label>
                  <Input
                    id="source-title"
                    placeholder="Enter a title for your note"
                    value={sourceTitle}
                    onChange={(e) => setSourceTitle(e.target.value)}
                    data-testid="input-onboarding-source-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="source-content">Content</Label>
                  <Textarea
                    id="source-content"
                    placeholder="Enter your notes, ideas, or content..."
                    value={sourceContent}
                    onChange={(e) => setSourceContent(e.target.value)}
                    rows={3}
                    data-testid="input-onboarding-source-content"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="source-url">
                  {sourceType === "youtube" ? "YouTube URL" : "Article URL"}
                </Label>
                <Input
                  id="source-url"
                  placeholder={sourceTypes.find(s => s.type === sourceType)?.placeholder}
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  data-testid="input-onboarding-source-url"
                />
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(3)}
                className="flex-1"
                data-testid="button-skip-source"
              >
                Skip for now
              </Button>
              <Button 
                onClick={handleAddSource} 
                className="flex-1"
                disabled={createSourceMutation.isPending}
                data-testid="button-onboarding-add-source"
              >
                {createSourceMutation.isPending ? (
                  "Adding..."
                ) : (
                  <>
                    Add Source <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4 text-center">
            <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
              <Rocket className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">You're All Set!</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Your knowledge base is ready. Start chatting with your AI assistant.
              </p>
            </div>

            <Card className="bg-muted/50">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Space: "{createdSpace?.name}"</span>
                </div>
                {createdSource && (
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-500" />
                    <span>Source: "{createdSource.title}"</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1"
                data-testid="button-explore-later"
              >
                Explore Later
              </Button>
              <Button 
                onClick={handleComplete}
                className="flex-1"
                data-testid="button-start-chatting"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Start Chatting
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-onboarding">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Welcome to Deeply Smart
          </DialogTitle>
          <DialogDescription>
            Let's get you started in 60 seconds
          </DialogDescription>
        </DialogHeader>

        {/* DS EDIT 12 — CMS-authored tour shows first when live slides exist */}
        {!tourDone && tourSlides.length > 0 ? (
          <OnboardingTourSlides slides={tourSlides} onDone={() => setTourDone(true)} />
        ) : (
        <div className="space-y-4">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Step {currentStep} of {steps.length}</span>
              <span>{Math.round(progress)}% complete</span>
            </div>
            <Progress value={progress} className="h-2" data-testid="progress-onboarding" />
          </div>

          {/* Step Indicators */}
          <div className="flex justify-between">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`flex flex-col items-center gap-1 ${
                  step.id <= currentStep ? "opacity-100" : "opacity-40"
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    step.id < currentStep
                      ? "bg-green-500 text-white"
                      : step.id === currentStep
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted"
                  }`}
                >
                  {step.id < currentStep ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <step.icon className="h-4 w-4" />
                  )}
                </div>
                <span className="text-[10px] text-center max-w-[60px]">{step.title}</span>
              </div>
            ))}
          </div>

          {/* Step Content */}
          <div className="pt-2">
            {renderStepContent()}
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
